import { Link } from 'react-router-dom';
import {
  CaretRight,
  Check,
  Pencil,
  Plus,
  Star,
  Trash,
  Vault,
} from '@phosphor-icons/react';

import pt from '../../locales/pt';
import { paths } from '../../routes/paths';
import { formatEUR } from '../../utils/money';
import { formatPotProgressLine } from '../../lib/annualCalendarUtils';
import { resolveOccasionalIcon } from '../../lib/annualOccasionalSuggestions';
import { cn } from '../../utils/cn';
import { ProgressBar } from '../common/ProgressBar';
import { ExpensesSectionLabel } from '../expenses/ExpensesSectionLabel';

/**
 * `.pen` → `ds/row/occasional-item` (master `SYczs`). O cartão-linha é sempre
 * neutro (`bg-card` / `border-border`) em todos os tipos — só o chip de ícone,
 * os badges e o bloco de cofre recebem tom (warning para "Dever", sage para
 * "Sonho"). Bloco de cofre: sage quando incompleto, success quando completo —
 * confirmado em ambos os cartões (dever e sonho) e em light/dark.
 */
function PotBlock({ item, potLabel }) {
  const hasPot = Boolean(item.has_pot ?? item.pot_id);
  if (!hasPot) return null;

  const progress = formatPotProgressLine(item);
  const tone = progress.complete
    ? {
        wrap: 'border-success-500 bg-success-tint',
        text: 'text-success-fg',
      }
    : {
        wrap: 'border-sage-border bg-sage-tint',
        text: 'text-sage-on-tint',
      };

  return (
    <div className={cn('flex flex-col gap-space-2 rounded-ds-md border p-space-3', tone.wrap)}>
      <div className="flex items-center justify-between gap-space-2">
        <span className={cn('inline-flex items-center gap-space-1 type-chip font-bold', tone.text)}>
          <Vault className="h-3.5 w-3.5 shrink-0" aria-hidden />
          {potLabel}
        </span>
        <span className={cn('type-chip font-bold tabular-nums', tone.text)}>
          {Math.round(progress.pct)}%
        </span>
      </div>
      <ProgressBar
        value={progress.pct}
        tone={progress.complete ? 'success' : 'sage'}
        className="h-1.5"
      />
      <div className="flex items-center justify-between gap-space-2 type-chip">
        {progress.complete ? (
          <span className="inline-flex items-center gap-space-1 font-semibold text-success-fg">
            <Check className="h-3.5 w-3.5 shrink-0" aria-hidden />
            {progress.primary}
          </span>
        ) : (
          <span />
        )}
        <span className={cn('shrink-0 tabular-nums', tone.text)}>{progress.secondary}</span>
      </div>
    </div>
  );
}

export function AnnualDutyCard({
  item,
  monthName,
  canEdit,
  onCreatePotFor,
  onEditItem,
  onDeleteItem,
  month,
}) {
  const amount = Number(item.amount || 0);
  const hasPot = Boolean(item.has_pot ?? item.pot_id);
  const Icon = resolveOccasionalIcon(item);

  return (
    <article className="flex flex-col gap-space-3 rounded-ds-lg border border-border bg-card p-space-3">
      <div className="flex items-center gap-space-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-ds-md bg-warning-tint text-warning-fg">
          <Icon className="h-5 w-5" aria-hidden />
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-space-1">
          <p className="type-body-sm font-semibold text-foreground">{item.name}</p>
          <div className="flex flex-wrap items-center gap-space-1">
            <span className="rounded-ds-sm bg-warning-tint px-space-2 py-space-1 type-chip font-bold text-warning-fg">
              {pt.calendar.monthDetail.badgeDuty}
            </span>
            <span className="rounded-ds-sm border border-border bg-muted px-space-2 py-space-1 type-chip font-bold tracking-wide text-muted-foreground">
              {monthName}
            </span>
          </div>
        </div>
        <p className="type-display-row shrink-0 text-foreground">{formatEUR(amount)}</p>
      </div>

      <PotBlock item={item} potLabel={pt.calendar.monthDetail.smoothingPot} />

      {canEdit ? (
        <div className="flex items-center justify-end gap-space-1 border-t border-border pt-space-2">
          {!hasPot ? (
            <button
              type="button"
              className="type-chip mr-auto font-semibold text-sage-on-tint hover:underline"
              onClick={() => onCreatePotFor?.(item.occasional_id)}
            >
              {pt.calendar.activatePot}
            </button>
          ) : (
            <span className="mr-auto" />
          )}
          <button
            type="button"
            className="rounded p-1 text-muted-foreground hover:bg-muted"
            onClick={() => onEditItem?.(item, month)}
            aria-label={pt.calendar.edit}
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            className="rounded p-1 text-muted-foreground hover:bg-danger-50 hover:text-danger-600"
            onClick={() => onDeleteItem?.(item)}
            aria-label={pt.calendar.delete}
          >
            <Trash className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : null}
    </article>
  );
}

export function AnnualDreamCard({ item, monthName }) {
  const amount = Number(item.amount || 0);

  return (
    <article className="flex flex-col gap-space-3 rounded-ds-lg border border-border bg-card p-space-3">
      <div className="flex items-center gap-space-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-ds-md bg-sage-tint text-sage-on-tint">
          <Star className="h-5 w-5" aria-hidden />
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-space-1">
          <p className="type-body-sm font-semibold text-foreground">{item.name}</p>
          <div className="flex flex-wrap items-center gap-space-1">
            <span className="rounded-ds-sm bg-sage-tint px-space-2 py-space-1 type-chip font-bold text-sage-on-tint">
              {pt.calendar.monthDetail.badgeDream}
            </span>
            <span className="rounded-ds-sm border border-border bg-muted px-space-2 py-space-1 type-chip font-bold tracking-wide text-muted-foreground">
              {monthName}
            </span>
          </div>
        </div>
        <p className="type-display-row shrink-0 text-foreground">{formatEUR(amount)}</p>
      </div>

      <PotBlock item={item} potLabel={pt.calendar.monthDetail.goalPot} />

      {item.goal_id ? (
        <Link
          to={paths.goal(item.goal_id)}
          className="inline-flex items-center gap-space-1 type-chip font-semibold text-sage-on-tint hover:underline"
        >
          {pt.calendar.monthDetail.viewGoal}
          <CaretRight className="h-3 w-3" aria-hidden />
        </Link>
      ) : null}
    </article>
  );
}

export function AnnualMonthDetail({
  month,
  selectedMonth,
  canEdit,
  onCreatePotFor,
  onEditItem,
  onDeleteItem,
  onViewYear,
  onAddInMonth,
}) {
  if (!month) return null;

  const items = month.items || [];
  const duties = items.filter((item) => (item.item_type || 'occasional') === 'occasional');
  const dreams = items.filter((item) => item.item_type === 'goal');
  const hint = pt.calendar.monthDetail.title.replace('{month}', '').trim();

  return (
    <section className="flex flex-col gap-space-3">
      <div className="flex items-center gap-space-2">
        <div className="min-w-0 flex-1">
          <ExpensesSectionLabel title={month.month_name} hint={hint} hintAlways />
        </div>
        <button
          type="button"
          onClick={onViewYear}
          className="inline-flex shrink-0 items-center gap-space-1 type-chip font-semibold text-sage-on-tint hover:underline"
        >
          {pt.calendar.monthDetail.viewYear}
          <CaretRight className="h-3 w-3" aria-hidden />
        </button>
      </div>

      <div className="flex flex-col gap-space-3">
        {duties.map((item) => (
          <AnnualDutyCard
            key={item.occasional_id}
            item={item}
            month={selectedMonth}
            monthName={month.month_name}
            canEdit={canEdit}
            onCreatePotFor={onCreatePotFor}
            onEditItem={onEditItem}
            onDeleteItem={onDeleteItem}
          />
        ))}
        {dreams.map((item) => (
          <AnnualDreamCard key={item.goal_id} item={item} monthName={month.month_name} />
        ))}
        {items.length === 0 ? (
          <div className="rounded-ds-lg border border-dashed border-border bg-muted/40 px-space-4 py-space-5 text-center">
            <p className="type-body-sm leading-relaxed text-muted-foreground">
              {pt.calendar.monthDetail.emptyMonth}
            </p>
            {onAddInMonth ? (
              <button
                type="button"
                onClick={() => onAddInMonth(selectedMonth)}
                className="mt-space-3 inline-flex items-center gap-space-1 rounded-ds-full border border-sage-600 px-space-3 py-space-2 type-chip font-semibold text-sage-on-tint hover:bg-sage-tint"
              >
                <Plus className="h-3.5 w-3.5" aria-hidden />
                {pt.calendar.monthDetail.emptyMonthCta}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}
